import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';
import {
  SUPPORT_ATTACHMENT_ACCEPT,
  formatSupportAttachmentSize,
  prepareSupportAttachment,
  revokeSupportAttachmentPreview,
  type SupportAttachmentSelection,
} from '@/lib/supportAttachments';
import { SUPPORT_MESSAGE_MAX_LENGTH } from '@/lib/supportChat';

import { Paperclip, SendHorizonal, X } from 'lucide-react';
import { type ChangeEvent, useEffect, useRef, useState } from 'react';

export interface SupportMessageComposerSubmission {
  body: string;
  attachment?: File | null;
}

interface SupportMessageComposerProps {
  disabled?: boolean;
  sending?: boolean;
  onSend: (submission: SupportMessageComposerSubmission) => Promise<boolean | void>;
  placeholder?: string;
  helperText?: string;
  sendLabel?: string;
  compact?: boolean;
  allowAttachments?: boolean;
}

export function SupportMessageComposer({
  disabled = false,
  sending = false,
  onSend,
  placeholder = 'Type your message to support…',
  helperText = 'Please write your message.',
  sendLabel = 'Send',
  compact = false,
  allowAttachments = false,
}: SupportMessageComposerProps) {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [message, setMessage] = useState('');
  const [attachment, setAttachment] = useState<SupportAttachmentSelection | null>(null);
  const [validatingAttachment, setValidatingAttachment] = useState(false);

  useEffect(() => {
    return () => {
      revokeSupportAttachmentPreview(attachment?.previewUrl);
    };
  }, [attachment?.previewUrl]);

  const normalizedMessage = message.trim();
  const canSend =
    !disabled &&
    !sending &&
    !validatingAttachment &&
    normalizedMessage.length <= SUPPORT_MESSAGE_MAX_LENGTH &&
    (normalizedMessage.length > 0 || Boolean(attachment));

  const clearAttachment = () => {
    revokeSupportAttachmentPreview(attachment?.previewUrl);
    setAttachment(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSelectAttachment = async (event: ChangeEvent<HTMLInputElement>) => {

    const nextFile = event.target.files?.[0];
    if (!nextFile) {
      return;
    }

    setValidatingAttachment(true);

    try {
      const nextAttachment = await prepareSupportAttachment(nextFile);
      revokeSupportAttachmentPreview(attachment?.previewUrl);
      setAttachment(nextAttachment);
    } catch (error) {
      toast({
        title: 'Attachment not allowed',
        description: error instanceof Error ? error.message : 'Please choose a supported image or text file.',
        variant: 'destructive',
      });
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } finally {
      setValidatingAttachment(false);
    }
  };

  const handleSubmit = async () => {
    if (!canSend) {
      return;
    }

    const result = await onSend({
      body: normalizedMessage,
      attachment: attachment?.file || null,
    });

    if (result === false) {
      return;
    }

    setMessage('');
    clearAttachment();
  };

  return (
    <div className="border-t border-border/70 px-5 py-4">
      <div className="rounded-2xl border border-border/70 bg-background p-3 shadow-sm">
        {attachment ? (
          <div className="mb-3 flex items-start gap-3 rounded-xl border border-border/70 bg-muted/30 p-3">
            {attachment.previewUrl ? (
              <img
                src={attachment.previewUrl}
                alt={attachment.fileName}
                className="h-14 w-14 rounded-lg object-cover"
              />
            ) : (
              <div className="flex h-14 w-14 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                <Paperclip className="h-4 w-4" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-foreground">{attachment.fileName}</p>
              <p className="text-xs text-muted-foreground">
                {attachment.kind === 'image' ? 'Image' : 'Text file'} • {formatSupportAttachmentSize(attachment.sizeBytes)}
              </p>
            </div>
            <Button type="button" variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={clearAttachment}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : null}

        <Textarea
          value={message}
          onChange={(event) => setMessage(event.target.value)}
          onKeyDown={async (event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              await handleSubmit();
            }
          }}
          placeholder={placeholder}
          disabled={disabled || sending || validatingAttachment}
          className={`${compact ? 'min-h-[64px]' : 'min-h-[96px]'} resize-none border-0 bg-transparent px-0 py-0 shadow-none focus-visible:ring-0`}
          maxLength={SUPPORT_MESSAGE_MAX_LENGTH}
        />
        <div className={`mt-3 flex flex-col ${compact ? 'gap-2' : 'gap-3'} sm:flex-row sm:items-end sm:justify-between`}>
          <div className="text-xs text-muted-foreground">
            <p>{helperText}</p>
            <p>{message.length}/{SUPPORT_MESSAGE_MAX_LENGTH} characters</p>
          </div>
          <div className="flex items-center gap-2 self-end">
            {allowAttachments ? (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={SUPPORT_ATTACHMENT_ACCEPT}
                  className="hidden"
                  onChange={handleSelectAttachment}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={disabled || sending || validatingAttachment}
                >
                  <Paperclip className="h-4 w-4" />
                  Attach
                </Button>
              </>
            ) : null}
            <Button type="button" onClick={() => void handleSubmit()} disabled={!canSend} className="gap-2 bg-emerald-600 hover:bg-emerald-700">
              <SendHorizonal className="h-4 w-4" />
              {sending ? `${sendLabel}...` : sendLabel}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
